'use client'

import ConfirmModal from '@/components/confirm-modal'
import { ModalContainer, ModalActions } from '@/components/container/container'
import { BackdropLoading } from '@/components/backdrop-loading'
import RichTextEditor from '@/components/rich-text-editor'
import '@/components/rich-text-editor/styles.css'
import {
    EmailAccount,
    disconnectEmailAccount,
    getEmailAccounts,
    getOAuthUrl,
    getAccountSignature,
    updateAccountSignature,
} from '@/lib/api/administrative/inbox-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdEdit,
    MdEmail,
    MdLinkOff,
} from 'react-icons/md'
import { FaGoogle } from 'react-icons/fa6'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    ACTIVE: { label: 'Ativa', className: 'badge-success' },
    DISCONNECTED: { label: 'Desconectada', className: 'badge-warning' },
    ERROR: { label: 'Erro', className: 'badge-error' },
}

export default function InboxAccountsPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'administrative',
        'inbox',
    )

    const [accounts, setAccounts] = useState<EmailAccount[]>([])
    const [loading, setLoading] = useState(true)
    const [connecting, setConnecting] = useState(false)
    const [disconnectModalId, setDisconnectModalId] = useState<number | null>(
        null,
    )
    const [disconnecting, setDisconnecting] = useState(false)

    // Assinatura
    const [signatureModalAccountId, setSignatureModalAccountId] = useState<
        number | null
    >(null)
    const [signatureHtml, setSignatureHtml] = useState('')
    const [loadingSignature, setLoadingSignature] = useState(false)
    const [savingSignature, setSavingSignature] = useState(false)

    useEffect(() => {
        loadAccounts()
    }, [])

    useEffect(() => {
        const connected = searchParams.get('connected')
        const email = searchParams.get('email')
        const error = searchParams.get('error')

        if (connected === 'true' && email) {
            toast.success(`Conta ${email} conectada com sucesso`)
            loadAccounts()
        }
        if (error) {
            toast.error(`Erro ao conectar conta: ${error}`)
        }
    }, [searchParams])

    const loadAccounts = async () => {
        setLoading(true)
        try {
            const response = await getEmailAccounts()
            if (response.success) {
                setAccounts(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar contas')
            }
        } catch {
            toast.error('Erro ao carregar contas')
        } finally {
            setLoading(false)
        }
    }

    const handleConnectGmail = async () => {
        setConnecting(true)
        try {
            const response = await getOAuthUrl()
            if (response.success && response.result?.url) {
                window.location.href = response.result.url
            } else {
                toast.error(response.error || 'Erro ao gerar URL de conexão')
                setConnecting(false)
            }
        } catch {
            toast.error('Erro ao conectar com o Gmail')
            setConnecting(false)
        }
    }

    const handleDisconnect = async () => {
        if (!disconnectModalId) return
        setDisconnecting(true)
        try {
            const response = await disconnectEmailAccount(disconnectModalId)
            if (response.success) {
                toast.success('Conta desconectada com sucesso')
                loadAccounts()
            } else {
                toast.error(response.error || 'Erro ao desconectar conta')
            }
        } catch {
            toast.error('Erro ao desconectar conta')
        } finally {
            setDisconnecting(false)
            setDisconnectModalId(null)
        }
    }

    const openSignatureEditor = async (accountId: number) => {
        setSignatureModalAccountId(accountId)
        setLoadingSignature(true)
        try {
            const response = await getAccountSignature(accountId)
            if (response.success && response.result) {
                setSignatureHtml(response.result.signatureHtml || '')
            } else {
                setSignatureHtml('')
            }
        } catch {
            setSignatureHtml('')
        } finally {
            setLoadingSignature(false)
        }
    }

    const handleSaveSignature = async () => {
        if (!signatureModalAccountId) return
        setSavingSignature(true)
        try {
            const response = await updateAccountSignature(
                signatureModalAccountId,
                signatureHtml,
            )
            if (response.success) {
                toast.success('Assinatura atualizada')
                setSignatureModalAccountId(null)
            } else {
                toast.error(
                    response.error || 'Erro ao atualizar assinatura',
                )
            }
        } catch {
            toast.error('Erro ao atualizar assinatura')
        } finally {
            setSavingSignature(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando contas de email...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('/administrative/inbox')}
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdEmail className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Contas de Email
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {accounts.length}{' '}
                            {accounts.length === 1
                                ? 'conta conectada'
                                : 'contas conectadas'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={handleConnectGmail}
                        disabled={connecting}
                    >
                        {connecting ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <FaGoogle className="text-lg" />
                        )}
                        Conectar Gmail
                    </button>
                )}
            </div>

            {/* Lista de contas */}
            {accounts.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdEmail className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhuma conta conectada
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        Conecte uma conta do Gmail para centralizar seus emails
                        aqui
                    </p>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={handleConnectGmail}
                            disabled={connecting}
                        >
                            {connecting ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <FaGoogle className="text-lg" />
                            )}
                            Conectar Gmail
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {accounts.map(account => {
                        const statusInfo = STATUS_LABELS[account.status] || {
                            label: account.status,
                            className: 'badge-ghost',
                        }
                        return (
                            <div
                                key={account.id}
                                className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 flex flex-col gap-4"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        {account.picture ? (
                                            <img
                                                src={account.picture}
                                                alt={account.name || account.email}
                                                className="w-12 h-12 rounded-full"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                                                <FaGoogle className="text-xl text-secondary" />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-semibold text-base-content truncate">
                                                {account.name || account.email}
                                            </p>
                                            <p className="text-sm text-base-content/60 truncate">
                                                {account.email}
                                            </p>
                                        </div>
                                    </div>
                                    <span
                                        className={`badge badge-sm ${statusInfo.className}`}
                                    >
                                        {statusInfo.label}
                                    </span>
                                </div>

                                <div className="text-xs text-base-content/50 space-y-1">
                                    {account.connectedBy && (
                                        <p>
                                            Conectada por:{' '}
                                            {account.connectedBy.name}
                                        </p>
                                    )}
                                    {account.lastSyncAt && (
                                        <p>
                                            Ultimo sync:{' '}
                                            {format(
                                                new Date(account.lastSyncAt),
                                                "dd/MM/yyyy 'as' HH:mm",
                                                { locale: ptBR },
                                            )}
                                        </p>
                                    )}
                                    {account.lastError && (
                                        <p className="text-error truncate">
                                            {account.lastError}
                                        </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 self-end">
                                    {canUpdate && (
                                        <button
                                            className="btn btn-ghost btn-sm gap-1"
                                            onClick={() =>
                                                openSignatureEditor(account.id)
                                            }
                                        >
                                            <MdEdit className="text-base" />
                                            Assinatura
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            className="btn btn-ghost btn-sm text-error gap-1"
                                            onClick={() =>
                                                setDisconnectModalId(account.id)
                                            }
                                        >
                                            <MdLinkOff className="text-base" />
                                            Desconectar
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}

                    {/* Card de adicionar */}
                    {canCreate && (
                        <button
                            className="bg-base-100/50 rounded-2xl border-2 border-dashed border-base-300/50 p-6 flex flex-col items-center justify-center gap-3 hover:border-secondary/30 hover:bg-secondary/5 transition-all cursor-pointer min-h-[200px]"
                            onClick={handleConnectGmail}
                            disabled={connecting}
                        >
                            <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                                <MdAdd className="text-2xl text-secondary" />
                            </div>
                            <p className="text-sm font-medium text-base-content/60">
                                Conectar outra conta
                            </p>
                        </button>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={!!disconnectModalId}
                onClose={() => setDisconnectModalId(null)}
                onConfirm={handleDisconnect}
                title="Desconectar Conta"
                message="Tem certeza que deseja desconectar esta conta de email? Os emails sincronizados serão removidos."
                confirmButtonText="Desconectar"
                loading={disconnecting}
            />

            {/* Modal de Assinatura */}
            {signatureModalAccountId && (
                <dialog className="modal modal-open z-50">
                    <ModalContainer size="md">
                        <div className="flex items-center justify-between border-b border-base-200 pb-3 mb-4">
                            <h3 className="text-lg font-semibold">
                                Editar Assinatura
                            </h3>
                            <p className="text-xs text-base-content/50">
                                {accounts.find(
                                    a => a.id === signatureModalAccountId,
                                )?.email || ''}
                            </p>
                        </div>

                        {loadingSignature ? (
                            <div className="flex items-center justify-center py-16">
                                <span className="loading loading-spinner loading-md text-secondary"></span>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4 overflow-y-auto max-h-[60vh]">
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-xs font-semibold">
                                            Assinatura do email
                                        </span>
                                    </label>
                                    <RichTextEditor
                                        content={signatureHtml}
                                        onChange={setSignatureHtml}
                                        placeholder="Digite sua assinatura..."
                                        minHeight="250px"
                                    />
                                </div>

                                {signatureHtml && (
                                    <div className="border border-base-200 rounded-lg p-4">
                                        <p className="text-xs font-semibold text-base-content/50 mb-2">
                                            Pré-visualização
                                        </p>
                                        <div
                                            className="prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{
                                                __html: signatureHtml,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <ModalActions>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() =>
                                    setSignatureModalAccountId(null)
                                }
                                disabled={savingSignature}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSaveSignature}
                                disabled={savingSignature || loadingSignature}
                            >
                                {savingSignature
                                    ? 'Salvando...'
                                    : 'Salvar'}
                            </button>
                        </ModalActions>
                    </ModalContainer>
                    <form method="dialog" className="modal-backdrop">
                        <button
                            onClick={() =>
                                setSignatureModalAccountId(null)
                            }
                        >
                            close
                        </button>
                    </form>
                </dialog>
            )}

            <BackdropLoading visible={savingSignature} />
        </div>
    )
}
